// ===============================================
// File: src/contexts/VaultContext.jsx
// Safe wrapper around StorageContext
// - Works whether StorageProvider is mounted or not
// - Always saves in both Drive and Local modes
// ===============================================

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useStorage as _useStorage } from "@/contexts/StorageContext"; // <-- NOTE: plural "contexts"

const VaultCtx = createContext(null);

export function VaultProvider({ children }) {
  // Try to access StorageContext, but don't crash if it's not mounted yet.
  let storage = null;
  try {
    storage = _useStorage();
  } catch {
    storage = null;
  }

  // Derive simple flags + labels
  const connected = !!storage?.mode; // either localSynced or cloudDrive
  const baseDirName = storage?.workspaceName || "";
  const mode = storage?.mode || (connected ? "connected" : "local");

  const [ready, setReady] = useState(false);

  // React to StorageContext availability/changes
  useEffect(() => {
    setReady(true);
  }, [connected, baseDirName, mode]);

  // Map Vault actions to StorageContext when available; otherwise safe fallbacks.

  const getJSON = async (path, fallback) => {
    if (!storage?.loadJSON) return fallback;
    return storage.loadJSON(path, fallback);
  };

  // IMPORTANT: do NOT gate on storage.hasFolder — save for BOTH Drive & Local
  const setJSON = async (path, value) => {
    if (!storage?.saveJSON) return false;
    await storage.saveJSON(path, value); // mirrors to localStorage and writes to Drive/Folder
    return true;
  };

  const saveFile = async (relPath, blob, opts) => {
    if (!storage?.saveFile) return false;
    return storage.saveFile(relPath, blob, opts);
  };

  const getFileUrl = async (relPath) => {
    if (!storage?.getFileUrl) return null;
    return storage.getFileUrl(relPath);
  };

  const deleteFile = async (relPath) => {
    if (!storage?.deleteFile) return false;
    return storage.deleteFile(relPath);
  };

  const list = async () => {
    // Not implemented in this shim; callers can know their own keys.
    return {};
  };

  const exportAll = async () => {
    if (!storage?.exportAll) return { where: "none" };
    return storage.exportAll();
  };

  const importBackup = async (file) => {
    if (!storage?.importBackup) return { ok: false };
    return storage.importBackup(file);
  };

  const chooseFolder = async () => {
    if (!storage?.pickFolder) return false;
    return storage.pickFolder();
  };

  const unlinkFolder = async () => {
    if (!storage?.clearFolderSelection) return false;
    await storage.clearFolderSelection();
    return true;
  };

  const value = useMemo(
    () => ({
      ready,
      mode,
      hasFolder: storage?.hasFolder || false, // local folder only
      baseDirName,
      // core I/O
      getJSON,
      setJSON,
      // binary I/O
      saveFile,
      getFileUrl,
      deleteFile,
      list,
      exportAll,
      importBackup,
      // folder actions
      chooseFolder,
      unlinkFolder,
      // expose storage bits some UIs may want
      isDriveLinked: !!storage?.isDriveLinked,
      isDriveAuthed: !!storage?.isDriveAuthed,
    }),
    [
      ready,
      mode,
      baseDirName,
      storage?.hasFolder,
      storage?.isDriveLinked,
      storage?.isDriveAuthed,
    ]
  );

  return <VaultCtx.Provider value={value}>{children}</VaultCtx.Provider>;
}

export const useVault = () => useContext(VaultCtx);
